import { Box, Button, ExpandableSection, Link, SpaceBetween } from '@amzn/awsui-components-react';
import { LoadingBar } from '@amzn/awsui-chat-components-console';
import { useTranslation } from 'react-i18next';
import { WritingAssistantState } from '../../../../utils/aiContentTypes';
import { markdownToHtml } from '../../../../utils/markdownToHtml';
import './writingAssistant.scss';

interface WritingAssistantPanelProps {
  state: WritingAssistantState;
  canRewrite: boolean;
  rewriteDisabledReason?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  rewrittenDescription?: string;
  tips?: string;
  errorMessage?: string;
  /** False when the model turned the input down; turns off Accept so it can't be used. */
  isRewriteValid?: boolean;
  onRewrite: () => void;
  onAccept: () => void;
  onCancel: () => void;
  onGenerateAnother: () => void;
}

export function WritingAssistantPanel({
  state,
  canRewrite,
  rewriteDisabledReason,
  isCollapsed,
  onToggleCollapse,
  rewrittenDescription,
  tips,
  errorMessage,
  isRewriteValid = true,
  onRewrite,
  onAccept,
  onCancel,
  onGenerateAnother,
}: WritingAssistantPanelProps) {
  const { t } = useTranslation(['events', 'translation']);

  if (isCollapsed) {
    return (
      <button
        className="writing-assistant writing-assistant--collapsed"
        data-testid="writing-assistant-panel-collapsed"
        onClick={onToggleCollapse}
        aria-label={t('writingAssistant.expand')}
        type="button"
      >
        <span className="writing-assistant__collapsed-icon" aria-hidden="true">
          ✦
        </span>
        <span className="writing-assistant__collapsed-label">{t('writingAssistant.title')}</span>
      </button>
    );
  }

  return (
    <div className="writing-assistant" data-testid="writing-assistant-panel">
      <div className="writing-assistant__header">
        <div className="writing-assistant__header-row">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <span className="writing-assistant__icon" aria-hidden="true">
              ✦
            </span>
            <Box variant="h4" margin={{ bottom: 'n' }}>
              {t('writingAssistant.title')}
            </Box>
          </SpaceBetween>
          <Button
            variant="icon"
            iconName="close"
            ariaLabel={t('writingAssistant.collapse')}
            onClick={onToggleCollapse}
          />
        </div>
      </div>

      <div className="writing-assistant__content" aria-live="polite">
        {state === 'idle' && <IdleContent />}
        {state === 'loading' && <LoadingContent />}
        {state === 'result' && (
          <ResultContent rewrittenDescription={rewrittenDescription ?? ''} tips={tips ?? ''} />
        )}
        {state === 'tips' && <TipsContent tips={tips ?? ''} />}
        {state === 'error' && <ErrorContent errorMessage={errorMessage} />}
      </div>

      {state !== 'loading' && (
        <div className="writing-assistant__footer">
          {(state === 'idle' || state === 'tips' || state === 'error') && (
            <Button
              variant="primary"
              disabled={!canRewrite}
              disabledReason={rewriteDisabledReason ?? t('writingAssistant.minCharsHint')}
              onClick={onRewrite}
              iconName="gen-ai"
              data-testid="rewrite-description-btn"
            >
              {t('writingAssistant.rewriteButton')}
            </Button>
          )}
          {state === 'result' && (
            <SpaceBetween direction="horizontal" size="s">
              <Button variant="link" onClick={onCancel} data-testid="cancel-rewrite-btn">
                {t('writingAssistant.cancel')}
              </Button>
              <Button
                variant="normal"
                iconName="refresh"
                disabled={!canRewrite}
                disabledReason={rewriteDisabledReason ?? t('writingAssistant.minCharsHint')}
                onClick={onGenerateAnother}
                data-testid="generate-another-btn"
              >
                {t('writingAssistant.generateAnother')}
              </Button>
              <Button
                variant="primary"
                disabled={!isRewriteValid}
                disabledReason={t('writingAssistant.cannotAcceptRewrite')}
                onClick={onAccept}
                data-testid="accept-rewrite-btn"
              >
                {t('writingAssistant.acceptRewrite')}
              </Button>
            </SpaceBetween>
          )}
        </div>
      )}
    </div>
  );
}

function IdleContent() {
  const { t } = useTranslation(['events', 'translation']);

  return (
    <div className="writing-assistant__guidance">
      <Box variant="p" fontWeight="bold">
        {t('writingAssistant.guidanceHeader')}
      </Box>
      <ul className="writing-assistant__guidance-list">
        <li>
          <strong>{t('writingAssistant.guidanceBenefits')}</strong>{' '}
          {t('writingAssistant.guidanceBenefitsDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidanceTasks')}</strong>{' '}
          {t('writingAssistant.guidanceTasksDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidanceLogistics')}</strong>{' '}
          {t('writingAssistant.guidanceLogisticsDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidancePreWork')}</strong>{' '}
          {t('writingAssistant.guidancePreWorkDetail')}
        </li>
        <li>
          <strong>{t('writingAssistant.guidanceFaqs')}</strong>{' '}
          {t('writingAssistant.guidanceFaqsDetail')}
        </li>
      </ul>
      <Box variant="p" color="text-body-secondary">
        {t('writingAssistant.guidanceFooter')}
      </Box>
    </div>
  );
}

function LoadingContent() {
  const { t } = useTranslation(['events', 'translation']);

  return (
    <div className="writing-assistant__loading" data-testid="writing-assistant-loading">
      <Box variant="p">{t('writingAssistant.rewritingDescription')}</Box>
      <LoadingBar variant="gen-ai" />
    </div>
  );
}

const AI_FEEDBACK_URL =
  'https://sim.amazon.com/issues/create?template=a2a5eaa0-5b78-4070-9475-c11315d94ecc';

function FeedbackLink() {
  const { t } = useTranslation(['events', 'translation']);

  return (
    <Box variant="p">
      <strong>{t('writingAssistant.feedbackLabelBold')}</strong>{' '}
      <Link href={AI_FEEDBACK_URL} external variant="primary">
        {t('writingAssistant.feedbackLinkText')}
      </Link>
    </Box>
  );
}

function ResultContent({
  rewrittenDescription,
  tips,
}: {
  rewrittenDescription: string;
  tips: string;
}) {
  const { t } = useTranslation(['events', 'translation']);

  return (
    <SpaceBetween direction="vertical" size="m">
      <div className="writing-assistant__suggested-rewrite">
        <Box variant="small" color="text-status-warning" fontWeight="bold">
          <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
            <span className="writing-assistant__icon" aria-hidden="true">
              ✦
            </span>
            {t('writingAssistant.suggestedRewrite')}
          </SpaceBetween>
        </Box>
        <div
          className="writing-assistant__rewrite-preview"
          data-testid="rewrite-preview"
          dangerouslySetInnerHTML={{ __html: markdownToHtml(rewrittenDescription) }}
        />
      </div>

      <FeedbackLink />
      {tips && (
        <ExpandableSection
          headerText={t('writingAssistant.tipsHeader')}
          variant="footer"
          defaultExpanded={false}
          data-testid="tips-section"
        >
          <div
            className="writing-assistant__tips-content"
            dangerouslySetInnerHTML={{ __html: markdownToHtml(tips) }}
          />
        </ExpandableSection>
      )}
    </SpaceBetween>
  );
}

function TipsContent({ tips }: { tips: string }) {
  const { t } = useTranslation(['events', 'translation']);

  return (
    <SpaceBetween direction="vertical" size="m">
      <FeedbackLink />
      {tips && (
        <ExpandableSection
          headerText={t('writingAssistant.tipsHeader')}
          variant="footer"
          defaultExpanded
        >
          <div
            className="writing-assistant__tips-content"
            dangerouslySetInnerHTML={{ __html: markdownToHtml(tips) }}
          />
        </ExpandableSection>
      )}
    </SpaceBetween>
  );
}

function ErrorContent({ errorMessage }: { errorMessage?: string }) {
  const { t } = useTranslation(['events', 'translation']);

  return (
    <Box variant="p" color="text-status-error">
      {errorMessage ?? t('writingAssistant.genericError')}
    </Box>
  );
}
